/* LBRGT Produkt-Karussell - arrow navigation plus the mobile pagination dots.

   Progressive enhancement: the track is an ordinary overflow-x + scroll-snap strip, so it
   swipes and scrolls by keyboard without this file. The arrows ship with the hidden
   attribute and the dot row ships empty; both only come alive once this script has
   confirmed the track really overflows (scrollWidth > clientWidth). There are never dead
   controls, and a strip of four cards on a 1440 desktop keeps its arrows hidden.

   Three things this file does differently from assets/lbrgt-highlights.js, all three
   learned the hard way:

   1. <product-recommendations> (assets/main.js) fetches the section and then replaces its
      own innerHTML WITHOUT firing an event. shopify:section:load never sees it. The host is
      therefore watched with a MutationObserver({ childList: true }) - no subtree, because
      the dot row this script builds is a descendant, not a direct child, so rebuilding the
      dots can never re-trigger the observer.
   2. Observers live on the element, not in a module level WeakMap. theme-editor.js
      re-evaluates every section script on reload, and a second copy with a fresh module
      scope could no longer reach - or disconnect - what the first copy created.
   3. The active dot is derived from scrollLeft / clientWidth, not from an
      IntersectionObserver. The dots stand for PAGES, not for single cards, so there is no
      per-card entry to observe; and an observer callback must never trust
      entry.isIntersecting, which is already true at any overlap above zero - only
      entry.intersectionRatio against the threshold says what is actually in view. The
      page arithmetic sidesteps the question entirely and is exact. */
(function () {
  'use strict';

  /* The first copy stays alive and keeps re-syncing, so later copies stand down instead of
     installing a second set of document listeners. */
  if (window.lbrgtRecoLoaded) return;
  window.lbrgtRecoLoaded = true;

  var MOBILE_QUERY = '(max-width: 768.98px)';
  var EDGE_TOLERANCE = 2;
  var mobile = window.matchMedia(MOBILE_QUERY);
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* 2px tolerance throughout: scrollWidth and clientWidth are rounded, and a fractional
     slide pitch (13.33px gaps) otherwise reports a phantom overflow or a phantom page. */
  function measureMax(track) {
    return track.scrollWidth - track.clientWidth;
  }

  /* The cached value from the last refresh. The scroll handler runs on every scroll event and
     must not read scrollWidth/clientWidth each time; refresh re-measures whenever the track
     resizes or its contents are swapped. */
  function maxScroll(track) {
    return typeof track.lbrgtRecoMax === 'number' ? track.lbrgtRecoMax : measureMax(track);
  }

  /* The reachable snap stops, as scroll distance from the start. Arrows and dots both move
     on this one list, so a click on either always lands where the indicator says.

     Everything has to count in the same unit as the scroller. With scroll-snap-type:
     x mandatory the strip always comes to rest on a card boundary, so pages counted in
     clientWidth do not line up with where it actually stops. Measured: at 320 the stops are
     232/464/696 while pages of 320 put both 232 and 464 on dot 2 - the strip moves and the
     indicator stands still - and the last dot was unreachable altogether, because
     round(offset / clientWidth) never reaches the last index when
     frac(scrollWidth / clientWidth) <= 0.5 (broken at 430, accidentally fine at 320).

     A page therefore starts at the first card that no longer fits into the previous one, and
     the last stop is clamped to maxScroll - exactly as the snap algorithm clamps a snap
     position into the scroll range, which is what makes the end reachable in the first place.
     offsetLeft differences cancel out the offsetParent, and reading them as absolute travel
     keeps this correct in RTL, where the cards run the other way. */
  function snapStops(track) {
    var slides = track.children;
    var max = measureMax(track);
    if (slides.length < 2 || !track.clientWidth || max <= EDGE_TOLERANCE) return [];

    var origin = slides[0].offsetLeft;
    var stops = [0];
    var page = 0;

    for (var i = 1; i < slides.length; i++) {
      var start = Math.abs(slides[i].offsetLeft - origin);
      if (start + slides[i].offsetWidth <= page + track.clientWidth + EDGE_TOLERANCE) continue;

      page = Math.min(start, max);
      // a clamped tail can land on the stop before it; one dot per position, never two
      if (page - stops[stops.length - 1] <= EDGE_TOLERANCE) break;
      stops.push(page);
      if (page >= max - EDGE_TOLERANCE) break;
    }

    return stops;
  }

  /* The end beats the arithmetic: once the strip rests at maxScroll the last dot is the
     active one, whichever stop happens to be nearest. */
  function activeStop(stops, offset, max) {
    if (offset >= max - EDGE_TOLERANCE) return stops.length - 1;

    var best = 0;
    var bestDistance = Infinity;
    for (var i = 0; i < stops.length; i++) {
      var distance = Math.abs(stops[i] - offset);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    }
    return best;
  }

  // RTL scrolls into negative numbers in some engines
  function scrollOffset(track) {
    return Math.abs(track.scrollLeft);
  }

  function isRtl(track) {
    return window.getComputedStyle(track).direction === 'rtl';
  }

  function behavior() {
    return reduceMotion.matches ? 'auto' : 'smooth';
  }

  function getTrack(element) {
    var id = element.getAttribute('data-track');
    return id ? document.getElementById(id) : null;
  }

  /* --- arrows ------------------------------------------------------------ */

  function updateArrows(nav, track) {
    var prev = nav.querySelector('[data-dir="prev"]');
    var next = nav.querySelector('[data-dir="next"]');
    if (!prev || !next) return;

    var offset = scrollOffset(track);
    var prevOff = offset <= EDGE_TOLERANCE;
    var nextOff = offset >= maxScroll(track) - EDGE_TOLERANCE;

    /* The real disabled attribute, not aria-disabled - but a disabled button drops the focus
       to the <body> and the keyboard user loses their place, so it is handed to the sibling.

       Order matters twice over, and getting it wrong is silent. Which button holds the focus
       has to be read BEFORE disabled is assigned, because assigning it blurs the element.
       The focus has to move AFTER both flags are assigned, because focus() on a button that
       is still disabled from the previous pass does nothing at all - measured at 1440 with
       two pages, where prev was still disabled at the moment next died and the focus ended
       up on the body. */
    var prevHadFocus = document.activeElement === prev;
    var nextHadFocus = document.activeElement === next;

    prev.disabled = prevOff;
    next.disabled = nextOff;

    if (prevOff && !nextOff && prevHadFocus) next.focus();
    else if (nextOff && !prevOff && nextHadFocus) prev.focus();
  }

  /* Fewer than two stops means the strip cannot move, and that is a better overflow test
     than scrollWidth against clientWidth: a leftover of less than one card is not a page. */
  function applyArrows(nav, track) {
    var stops = track.lbrgtRecoStops || [];

    if (stops.length < 2) {
      // never leave the focus on a control that is about to disappear
      if (nav.contains(document.activeElement)) document.activeElement.blur();
      nav.setAttribute('hidden', '');
      return;
    }

    nav.removeAttribute('hidden');

    if (nav.dataset.lbrgtRecoBound !== 'true') {
      nav.dataset.lbrgtRecoBound = 'true';
      nav.addEventListener('click', function (event) {
        var button = event.target.closest('[data-dir]');
        if (!button || button.disabled) return;

        /* One click moves one stop, not one clientWidth. On desktop that is the same thing -
           one stop holds exactly the four cards that fill the content box - but on mobile a
           clientWidth is 1.85 cards, so the strip snapped somewhere between two stops and
           the dots had to skip. Reading the list through the element keeps this valid after
           a rebuild. */
        var current = track.lbrgtRecoStops || [];
        if (current.length < 2) return;
        var index = activeStop(current, scrollOffset(track), maxScroll(track));
        scrollToStop(track, index + (button.getAttribute('data-dir') === 'next' ? 1 : -1));
      });
    }

    updateArrows(nav, track);
  }

  /* --- dots -------------------------------------------------------------- */

  function clearDots(dots) {
    dots.textContent = '';
    dots.removeAttribute('role');
    dots.removeAttribute('aria-label');
    delete dots.dataset.lbrgtRecoPages;
  }

  function setActiveDot(dots, track) {
    var buttons = dots.children;
    var stops = track.lbrgtRecoStops;
    if (!buttons.length || !stops || !stops.length || !track.clientWidth) return;

    var index = activeStop(stops, scrollOffset(track), maxScroll(track));
    if (index > buttons.length - 1) index = buttons.length - 1;
    if (index < 0) index = 0;

    for (var i = 0; i < buttons.length; i++) {
      buttons[i].setAttribute('aria-current', i === index ? 'true' : 'false');
    }
  }

  /* The stops are already clamped into the scroll range, so there is nothing left to clamp
     here - and the target is a real snap position, which is why the strip ends up exactly
     where the dot says it does. The list is read through the element, so it is always the
     current one even after a rebuild. */
  function scrollToStop(track, index) {
    var stops = track.lbrgtRecoStops;
    if (!stops || index < 0 || index > stops.length - 1) return;
    var target = stops[index];
    if (isRtl(track)) target = -target;
    track.scrollTo({ left: target, behavior: behavior() });
  }

  function applyDots(dots, track) {
    // the design has no dots on desktop, so none are built there
    var stops = mobile.matches ? track.lbrgtRecoStops || [] : [];

    if (stops.length < 2) {
      clearDots(dots);
      return;
    }

    /* Idempotent: the editor re-runs this on every section reload and the ResizeObserver on
       every resize tick. The signature covers both the count and the clamped tail, so a
       changed last stop rebuilds even when the number of pages stayed the same. */
    var signature = stops.length + '@' + Math.round(stops[stops.length - 1]);
    if (dots.dataset.lbrgtRecoPages === signature) {
      setActiveDot(dots, track);
      return;
    }

    clearDots(dots);

    for (var i = 0; i < stops.length; i++) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'lbrgt-reco__dot';
      button.setAttribute('aria-label', 'Seite ' + (i + 1) + ' von ' + stops.length);
      button.setAttribute('aria-current', i === 0 ? 'true' : 'false');
      button.addEventListener(
        'click',
        (function (index) {
          return function () {
            scrollToStop(track, index);
          };
        })(i)
      );
      dots.appendChild(button);
    }

    /* role and aria-label are set together with the buttons and removed again in clearDots,
       so an empty container never announces a navigation that is not there. */
    dots.setAttribute('role', 'group');
    dots.setAttribute('aria-label', dots.getAttribute('data-dots-label') || 'Seitennavigation');
    dots.dataset.lbrgtRecoPages = signature;

    setActiveDot(dots, track);
  }

  /* --- wiring ------------------------------------------------------------ */

  function refresh(root) {
    /* The host has to stay in the DOM for main.js to fetch into, so the collapse of the dark
       strip hangs on this class. The Liquid renders it when there are no products; it comes
       off as soon as the fetched markup brings an inner, and goes back on if a later swap
       returns nothing. The :has() rule in the CSS does the same for engines without JS. */
    var inner = root.querySelector('.lbrgt-reco__inner');
    root.classList.toggle('lbrgt-reco--empty', !inner);

    var nav = root.querySelector('[data-lbrgt-reco-nav]');
    var dots = root.querySelector('[data-lbrgt-reco-dots]');
    var track = nav ? getTrack(nav) : dots ? getTrack(dots) : null;
    if (!track) return;

    /* One list, on the track, for both controls. It has to be measured before either of them
       is applied, and it lives on the element so any copy of this script can read it.
       lbrgtRecoMax is cached with it so the scroll handler never touches layout. */
    track.lbrgtRecoStops = snapStops(track);
    track.lbrgtRecoMax = measureMax(track);

    if (nav) applyArrows(nav, track);
    if (dots) applyDots(dots, track);

    /* Bound once per track. Both containers survive every rebuild - only their children are
       replaced - so the closure stays valid and no listener piles up.

       Updated synchronously, deliberately not coalesced through requestAnimationFrame. The
       first version held a "frame pending" flag and dropped every scroll event while it was
       set; measured headless, the very first frame was starved, the flag stayed up and the
       indicator froze on page 1 for the rest of the session - arrows included. Chrome also
       starves rAF in a background tab, and the work here is a scrollLeft read plus an aria
       attribute per dot, with maxScroll taken from the cache, so there is nothing to
       coalesce: scroll fires at most once per frame anyway. */
    if (track.dataset.lbrgtRecoBound !== 'true') {
      track.dataset.lbrgtRecoBound = 'true';
      track.addEventListener(
        'scroll',
        function () {
          if (nav && !nav.hasAttribute('hidden')) updateArrows(nav, track);
          if (dots) setActiveDot(dots, track);
        },
        { passive: true }
      );
    }

    // the viewport decides how many cards fit, so page count and overflow change with it
    if (window.ResizeObserver && !track.lbrgtRecoResize) {
      track.lbrgtRecoResize = new ResizeObserver(function () {
        refresh(root);
      });
      track.lbrgtRecoResize.observe(track);
    }
  }

  function watchSwap(root) {
    /* Only the custom element swaps its content; the collection variant renders a plain div
       that never changes. childList without subtree: the dot row is a descendant, so
       building dots cannot re-trigger this. */
    if (root.tagName !== 'PRODUCT-RECOMMENDATIONS') return;
    if (!window.MutationObserver || root.lbrgtRecoSwap) return;

    root.lbrgtRecoSwap = new MutationObserver(function () {
      /* The old track and its flags are gone with the old markup; only clean up what hangs
         on the root, then wire the fresh children. No focus is moved here - the
         recommendations are not a status message and must not steal the caret. */
      refresh(root);
    });
    root.lbrgtRecoSwap.observe(root, { childList: true });
  }

  function apply(root) {
    watchSwap(root);
    refresh(root);
  }

  function teardown(root) {
    if (root.lbrgtRecoSwap) {
      root.lbrgtRecoSwap.disconnect();
      root.lbrgtRecoSwap = null;
    }

    var nav = root.querySelector('[data-lbrgt-reco-nav]');
    var dots = root.querySelector('[data-lbrgt-reco-dots]');
    var track = nav ? getTrack(nav) : dots ? getTrack(dots) : null;

    if (track) {
      track.lbrgtRecoStops = null;
      track.lbrgtRecoMax = null;
      if (track.lbrgtRecoResize) {
        track.lbrgtRecoResize.disconnect();
        track.lbrgtRecoResize = null;
      }
    }

    if (nav) nav.setAttribute('hidden', '');
    if (dots) clearDots(dots);
  }

  function collect(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    var roots = Array.prototype.slice.call(context.querySelectorAll('.lbrgt-reco'));
    // a section:load event hands us the section wrapper itself, not an ancestor
    if (context !== document && context.classList && context.classList.contains('lbrgt-reco')) {
      roots.push(context);
    }
    return roots;
  }

  function sync(scope) {
    collect(scope).forEach(apply);
  }

  function init() {
    sync(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  var onViewportChange = function () {
    sync(document);
  };
  if (mobile.addEventListener) mobile.addEventListener('change', onViewportChange);
  else if (mobile.addListener) mobile.addListener(onViewportChange);

  // theme editor: reloading or picking the section must not duplicate arrows or dots
  document.addEventListener('shopify:section:load', function (event) {
    sync(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    collect(event.target).forEach(teardown);
  });
  document.addEventListener('shopify:block:select', function () {
    sync(document);
  });
})();
